 
import { keyBy } from 'lodash';

export const MAKE_MODEL_STATUS_MAP = {
  PendingReview: 0,
  Approved: 1,
  Rejected: 2,
  GSAReview: 3,
  Archived: 4,
};

export const MakeModelApprovalStatus = {
  Pending: 0,
  Approved: 1,
  Rejected: 2,
  GSAReview: 3,
  Archived: 4,
  PendingReview: 5,
};

export const ModelMakeStatuses = keyBy(
  [
    {
      label: 'Approved',
      value: 'Approved',
    },
    {
      label: 'Rejected',
      value: 'Rejected',
    },
    {
      label: 'Pending review',
      value: 'PendingReview',
    },
    {
      label: 'Archived',
      value: 'Archived',
    },
  ],
  'value',
);

export const MAKE_MODEL_APPROVAL_STATUS_MAP = {
  PendingReview: 0,
  Approved: 1,
  Rejected: 2,
  GSAReview: 3,
  Archived: 4,
};

export const ModelMakeApprovalStatuses = keyBy(
  [
    {
      label: 'Approved',
      value: 'Approved',
      showInFilter: true,
    },
    {
      label: 'Rejected',
      value: 'Rejected',
      showInFilter: true,
    },
    {
      label: 'Pending review',
      value: 'PendingReview',
      showInFilter: true,
    },
    {
      label: 'Archived',
      value: 'Archived',
      showInFilter: false,
    },
  ],
  'value',
);

export const MAKE_MODEL_CONTEXT = {
  PRE_BID: 'Pre-Bid',
  BID: 'Bid',
  CONTRACT: 'Contract',
};

export const STANDARD_ITEM_TAG = {
  Chassis: 'REQ_CHASSIS_MOD',
};

export const STATUS = {
  REJECTED: 'Rejected',
  PENDING_REVIEW: 'PendingReview',
  APPROVED: 'Approved',
};

// CONSTANT for ApprovedByUser
export const APPROVED_BY = {
  NHTSA: 'NHTSA',
  GSA_ENGINEER: 'GSA Engineer',
  DEFAULT: '-',
};

export const NHTSAMakeModelStatus = {
  Inactive: 0,
  Active: 1,
};

export const NHTSA_STATUS_FILTER_VALUES = [
  {
    label: 'Active',
    value: '1',
  },
  {
    label: 'Inactive',
    value: '0',
  },
]


export const NHTSA_STATUS_MAP = {
  [NHTSAMakeModelStatus.Inactive]: 'Inactive',
  [NHTSAMakeModelStatus.Active]: 'Active',
};

export const MAKE_MODEL_ENTRY_SOURCE = {
  NHTSA: 0,
  PRE_BID: 1,
  BID: 2,
  ENG: 3,
  CONTRACT: 4,
  VMS: 5,
  LEGACY: 6,
};

export const CATALOG_ENTRY_SOURCES = [
  MAKE_MODEL_ENTRY_SOURCE.NHTSA,
  MAKE_MODEL_ENTRY_SOURCE.PRE_BID,
  MAKE_MODEL_ENTRY_SOURCE.BID,
  MAKE_MODEL_ENTRY_SOURCE.ENG,
  MAKE_MODEL_ENTRY_SOURCE.CONTRACT,
];

// Visual indicator pill for each make/model entry source. variant maps to a
// component-library Tag variant, distinct per source.
export const MAKE_MODEL_ENTRY_SOURCE_BADGE = {
  NHTSA: { label: 'NHTSA', fullLabel: 'NHTSA', variant: 'Ready-Dark' },
  PRE_BID: { label: 'P-BID', fullLabel: 'Pre-bid', variant: 'Urgent-Medium' },
  BID: { label: 'BID', fullLabel: 'Bid', variant: 'Important-Dark' },
  ENG: { label: 'ENG', fullLabel: 'Engineering', variant: 'Urgent-Dark' },
  CONTRACT: { label: 'CONT', fullLabel: 'Contract', variant: 'Info-Dark' },
  VMS: { label: 'VMS', fullLabel: 'VMS', variant: 'Urgent-Light' },
  LEGACY: { label: 'LGCY', fullLabel: 'Legacy', variant: 'Inactive-Medium' },
};

// entrySource may arrive as a numeric code (0) or its enum name ('NHTSA');
// resolve either to its badge descriptor.
export const getEntrySourceBadge = (entrySource) => {
  if (entrySource === null || entrySource === undefined) return null;
  const key =
    typeof entrySource === 'number'
      ? Object.keys(MAKE_MODEL_ENTRY_SOURCE).find(
          (k) => MAKE_MODEL_ENTRY_SOURCE[k] === entrySource,
        )
      : entrySource;
  return MAKE_MODEL_ENTRY_SOURCE_BADGE[key] || null;
};

// Source filter checkbox options (label/value) for the Makes and models tab.
export const MAKE_MODEL_SOURCE_FILTER_VALUES = [
  { label: 'GSA Engineer', value: 'GSA_ENGINEER' },
  { label: 'NHTSA', value: 'NHTSA' },
  { label: 'Legacy', value: 'LEGACY' },
  { label: 'VMS', value: 'VMS' },
];

// Maps each Source filter option to the model entrySource codes it represents.
export const MAKE_MODEL_SOURCE_FILTER_MAP = {
  NHTSA: [MAKE_MODEL_ENTRY_SOURCE.NHTSA],
  GSA_ENGINEER: [
    MAKE_MODEL_ENTRY_SOURCE.PRE_BID,
    MAKE_MODEL_ENTRY_SOURCE.BID,
    MAKE_MODEL_ENTRY_SOURCE.ENG,
    MAKE_MODEL_ENTRY_SOURCE.CONTRACT,
  ],
  VMS: [MAKE_MODEL_ENTRY_SOURCE.VMS],
  LEGACY: [MAKE_MODEL_ENTRY_SOURCE.LEGACY],
};

// Result statuses returned by the addMakeModelByEngineer mutation. SUCCESS is
// a green toast; the ALREADY_* statuses are non-fatal and shown as a warning.
export const ADD_MAKE_MODEL_STATUS = {
  SUCCESS: 'SUCCESS',
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  ALREADY_SUBMITTED: 'ALREADY_SUBMITTED',
};

// Source dropdown options for the Add make and model modal. Only VMS is
// currently selectable; value is the entrySource code persisted on the model.
export const MAKE_MODEL_ADD_SOURCE_OPTIONS = [
  { label: 'VMS', value: MAKE_MODEL_ENTRY_SOURCE.VMS },
];

// Source filter checkbox options for the Make and model approval tab. Each
// value is the entrySource code the backend filters on (Pre bid 1, Bid 2,
// Contract 4, VMS 5).
export const MAKE_MODEL_APPROVAL_SOURCE_FILTER_VALUES = [
  { label: 'Pre bid', value: String(MAKE_MODEL_ENTRY_SOURCE.PRE_BID) },
  { label: 'Bid', value: String(MAKE_MODEL_ENTRY_SOURCE.BID) },
  { label: 'Contract', value: String(MAKE_MODEL_ENTRY_SOURCE.CONTRACT) },
  { label: 'VMS', value: String(MAKE_MODEL_ENTRY_SOURCE.VMS) },
];